import { useEffect, useState, useCallback, useMemo } from 'react';
import DocumentHeader from '@/components/DocumentHeader';
import track, { useTracking } from 'react-tracking';
import { SWS_HOST } from '@/constants';
import loadable from '@loadable/component';
import { OneColumn } from '@/components/Layout';
import GlobalStyle from '@/styled/styled.default';
import { useTheme, LoadingWarren } from '@simplywallst/ui-core';
import useUser from '@/hooks/useUser';
import ScrollLoader from '@/components/ScrollLoader';
import { Footer } from '@/components/Footer';
import { useMetaData } from '../../hooks/useMetaData';
import { useReduxStateForDiscover } from '../../hooks/useReduxStateForDiscover';
import { updateRule } from '@ducks/grid/utils';
import {
  ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
  ROUTE_INVESTING_IDEA_MARKET,
} from '@/constants/routes';
import DocumentSchema from '@/components/DocumentSchema';
import type {
  Corporation,
  ItemList,
  ListItem,
  SearchResultsPage,
} from 'schema-dts';

import { NoResultsPanelNewCompanies } from '../BasicHeading/components/NoResultsPanelNewCompanies';
import { BasicHeading } from '../BasicHeading';
import { useGqlUserScreener } from '@/hooks/useGridView/useGqlUserScreener';
import { transformUserScreenerGqlData } from '@/utilities/screener';
import { useInvestingIdeaScreener } from '@/hooks/useInvestingIdeaScreener';
import { useHrefLangMarkets } from '@/hooks/discover/useHrefLangMarkets/useHrefLangMarkets';
import { getPath } from '@/router/utils';
import { DEFAULT_INDUSTRY, DEFAULT_MARKET } from '../../constants';

const TileList = loadable(() => import('@/components/TileList'), {
  resolveComponent: (c) => c.TileList,
});

const StockTable = loadable(
  () => import('@/pages/Stocks/components/StockTable')
);
const Pagination = loadable(() => import('@/components/Pagination'));

const GuestRegistrationModalOnScroll = loadable(
  () => import('@/components/GuestRegistrationModalOnScroll')
);

type StocksView = 'table' | 'tile';

export const PAGE_SIZE = 36;

// Investing Ideas
// TODO: Further alignment with Stocks.tsx, eg: move view functions out to hook
const DiscoverPageV1 = () => {
  /**
   * Redux
   */
  const {
    id = '0',
    name: slug = 'name',
    market = DEFAULT_MARKET,
    industry = DEFAULT_INDUSTRY,
    pageQuery,
    canonicalUrl,
    navigateTo,
    getPaginatedRoute,
    onAdvancedFilters,
    preFetchCompany,
  } = useReduxStateForDiscover();

  const route =
    industry !== DEFAULT_INDUSTRY
      ? ROUTE_INVESTING_IDEA_MARKET_INDUSTRY
      : ROUTE_INVESTING_IDEA_MARKET;
  /** Hooks */
  const theme = useTheme();
  const { trackEvent } = useTracking();

  const {
    data: user,
    mutate: mutateUser,
    isFetched: isUserFetched,
  } = useUser();

  const hasInfiniteScroll = user.loggedIn && pageQuery === 1;
  const [stocksView, setStocksView] = useState<StocksView>(
    !user.loggedIn ? 'tile' : user.defaultStocksView
  );
  const [returnRecentCompaniesOnly, setReturnRecentCompaniesOnly] =
    useState(false);

  useEffect(() => {
    trackEvent({
      type: 'track',
      subject: 'page',
      action: 'landed',
      id,
    });
  }, [id, trackEvent]);

  useEffect(() => {
    if (isUserFetched && typeof stocksView === 'undefined' && user.loggedIn) {
      setStocksView(user.defaultStocksView);
    }
  }, [isUserFetched, user, stocksView]);

  const { data: userScreenerData, status: userScreenerStatus } =
    useGqlUserScreener({ id: parseFloat(id) });

  const {
    rules: rawRules,
    name,
    description,
    pageDescription,
    onlyMarketFiltered,
    crawlable,
    imageUrl,
  } = transformUserScreenerGqlData(userScreenerData);

  const [rules, setCurrentRules] = useState<string | undefined>(rawRules);

  const {
    companies,
    totalRecords,
    newCompaniesCount,
    totalPages,
    fetchNextPage,
    hasNextPage,
    isFetchingCompanies,
    isFetched,
    systemRestrictions,
    isMarketInRules,
  } = useInvestingIdeaScreener({
    rawRules,
    market,
    industry,
    gridViewId: id,
    initPage: pageQuery - 1,
    pageSize: PAGE_SIZE,
    isInfinite: hasInfiniteScroll,
    returnRecentCompaniesOnly,
  });

  useEffect(() => {
    if (isMarketInRules && market !== DEFAULT_MARKET) {
      navigateTo({
        method: 'replace',
        route,
        market: DEFAULT_MARKET,
        industry,
      });
    }
  }, [industry, isMarketInRules, market, navigateTo, route]);

  useEffect(() => {
    setCurrentRules(rawRules);
  }, [rawRules]);

  const updatedRules = useMemo(
    () =>
      typeof rules === 'undefined' || rules === ''
        ? undefined
        : updateRule(rules)(market, industry),
    [rules, market, industry]
  );

  const defaultRules = useMemo(
    () =>
      typeof rawRules === 'undefined' || rawRules === ''
        ? undefined
        : updateRule(rawRules)(market, industry),
    [rawRules, market, industry]
  );

  const isFetching = userScreenerStatus === 'pending' || isFetchingCompanies;
  const meta = useMetaData({
    name,
    market,
    industry,
    totalRecords,
    page: pageQuery,
    hasHiddenCountry: isMarketInRules,
  });

  const handleViewSelect = useCallback(
    (value: StocksView) => {
      setStocksView(value);
      if (user.loggedIn) {
        mutateUser({
          settings: {
            stocks_view: value,
          },
        });
      }
    },
    [mutateUser, user]
  );

  const { restrictionCode, maxResultCountForRecentCompanyFeature } =
    systemRestrictions ?? {};
  const noDataContent = returnRecentCompaniesOnly ? (
    <NoResultsPanelNewCompanies
      rules={updatedRules}
      reason={restrictionCode}
      maxResultCountForRecentCompanyFeature={
        maxResultCountForRecentCompanyFeature
      }
      name={name}
      description={description ?? ''}
    />
  ) : null;

  const payload = {
    id: id || '0',
    name: slug || 'slug',
    industry,
  };

  const { hrefLangMarkets } = useHrefLangMarkets(route, payload);

  const hasMarkets = !isMarketInRules;

  const globalUrl = getPath({
    type: route,
    payload: {
      market: DEFAULT_MARKET,
      id: id || '0',
      name: slug || 'slug',
      industry,
    },
  });

  return (
    <>
      <OneColumn>
        <GlobalStyle background={theme.color.charcoal} />
        <DocumentHeader
          title={meta.documentTitle}
          // TODO: When page is noindex'ed, we should also not allow page to be added to the hrefLang ring. Requires a higher level change.
          allowCrawlers={meta.allowCrawlers && crawlable}
          description={description ?? ''}
        >
          {companies.length > 0 && (
            <link
              rel="canonical"
              href={`${SWS_HOST}${canonicalUrl}${
                pageQuery === 1 ? '' : '?page=' + pageQuery
              }`}
            />
          )}
          {pageQuery === 1 && (
            <link
              rel="alternate"
              hrefLang="x-default"
              href={`${SWS_HOST}${globalUrl}`}
            />
          )}
          {pageQuery === 1 && hasMarkets && hrefLangMarkets}
        </DocumentHeader>
        {
          // Show DocumentSchema for logged out users so ssr and hydration is synced
          user.loggedIn === false && RUNTIME_ENV !== 'native' && (
            <DocumentSchema<SearchResultsPage>
              item={{
                '@context': 'https://schema.org',
                '@type': 'SearchResultsPage',
                identifier: `${SWS_HOST}${canonicalUrl}#webpage`,
                url: `${SWS_HOST}${canonicalUrl}`,
                mainEntity: [
                  {
                    '@type': 'ItemList',
                    name: meta.documentTitle,
                    itemListOrder: 'http://schema.org/ItemListOrderDescending',
                    itemListElement: companies.map((n, i) => {
                      return {
                        '@type': 'ListItem',
                        position: i + 1,
                        item: {
                          '@type': 'Corporation',
                          name: n.shortName,
                          legalName: n.legalName,
                          tickerSymbol: n.tickerSymbol,
                          url: `${SWS_HOST}${n.canonicalUrl}`,
                          logo: n.logoUrl,
                          foundingDate: n.yearFounded,
                          description: n.shortDescription,
                        } satisfies Corporation,
                      } satisfies ListItem;
                    }),
                  } satisfies ItemList,
                ],
                isPartOf: {
                  '@type': 'WebSite',
                  url: SWS_HOST,
                  name: 'Simply Wall St',
                },
              }}
            />
          )
        }
        <BasicHeading
          totalRecords={totalRecords}
          stocksView={stocksView}
          onViewSelect={handleViewSelect}
          onlyMarketFiltered={onlyMarketFiltered || false}
          defaultRules={defaultRules}
          rules={updatedRules ?? ''}
          id={id}
          name={meta.title}
          imageUrl={imageUrl}
          description={description ?? ''}
          pageDescription={pageDescription ?? ''}
          selectedMarket={market}
          selectedIndustry={industry}
          setCurrentRules={setCurrentRules}
          hideMarketFilter={isMarketInRules}
          rawRules={rawRules}
          onMarketUpdate={(market) =>
            navigateTo({
              route,
              market,
              industry,
            })
          }
          onIndustryUpdate={(nextIndustry) =>
            navigateTo({
              route:
                nextIndustry !== DEFAULT_INDUSTRY
                  ? ROUTE_INVESTING_IDEA_MARKET_INDUSTRY
                  : ROUTE_INVESTING_IDEA_MARKET,
              market,
              industry: nextIndustry,
            })
          }
          onAdvancedFilterClick={() => onAdvancedFilters(updatedRules)}
          slug={slug}
          returnRecentCompaniesOnly={returnRecentCompaniesOnly}
          onOnlyRecentSearchesUpdate={
            pageQuery === 1 ? setReturnRecentCompaniesOnly : undefined
          }
          isLoading={isFetching}
          newCompaniesCount={newCompaniesCount}
          maxResultCount={maxResultCountForRecentCompanyFeature || null}
        />
        {hasInfiniteScroll ? (
          <>
            <ScrollLoader
              handleLoadRequest={() => {
                if (!isFetching && companies.length && hasNextPage) {
                  fetchNextPage();
                }
              }}
            >
              {() =>
                stocksView === 'table' ? (
                  <StockTable
                    companies={companies}
                    industry={industry}
                    market={market}
                    isFetching={isFetching}
                    isFetched={isFetched}
                    onLongHover={preFetchCompany}
                    hideIndustryCol={onlyMarketFiltered || false}
                    noDataContent={noDataContent}
                  />
                ) : (
                  <TileList
                    companies={companies}
                    onLongHover={preFetchCompany}
                    isFetching={isFetchingCompanies}
                    isFetched={isFetched}
                    noDataContent={noDataContent}
                  />
                )
              }
            </ScrollLoader>
            {isFetching && (
              <div className="pb-6 pt-4">
                <LoadingWarren width={70} />
              </div>
            )}
          </>
        ) : (
          <>
            {stocksView === 'table' ? (
              <StockTable
                companies={companies}
                industry={industry}
                market={market}
                isFetching={isFetching}
                isFetched={isFetched}
                onLongHover={preFetchCompany}
                hideIndustryCol={onlyMarketFiltered || false}
                noDataContent={noDataContent}
              />
            ) : (
              <TileList
                companies={companies}
                onLongHover={preFetchCompany}
                isFetching={isFetchingCompanies}
                isFetched={isFetched}
                noDataContent={noDataContent}
              />
            )}
            {totalPages > 1 && (
              <div className="mt-7">
                <Pagination
                  currentPage={pageQuery}
                  totalPages={totalPages}
                  actionLink={(page) => getPaginatedRoute(route, page)}
                  loading={isFetching}
                />
              </div>
            )}
          </>
        )}
        {!user.loggedIn && <GuestRegistrationModalOnScroll />}
        {(!hasInfiniteScroll || totalRecords <= PAGE_SIZE) && <Footer />}
      </OneColumn>
    </>
  );
};

export default track(
  {
    type: 'track',
    subject: 'page',
    action: 'landed',
  },
  { dispatchOnMount: true }
)(DiscoverPageV1) as typeof DiscoverPageV1;
